import React, { useEffect, useState } from "react";
import CheckboxInput from "./CheckboxInput";

interface CheckboxOption {
  id: number;
  label: string;
}

interface CheckboxGroupInputProps {
  id: string;
  options: CheckboxOption[];
  onChange?: (selectedValues: Record<number, boolean>) => void;
  isResetCheckboxes?: boolean;
  setIsResetCheckboxes?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CheckboxGroupInput: React.FC<CheckboxGroupInputProps> = ({
  id,
  options,
  onChange,
  isResetCheckboxes,
  setIsResetCheckboxes,
}) => {
  // Initialize checkboxes with all options set to false, excluding selectAll
  const [checkboxes, setCheckboxes] = useState<Record<number, boolean>>(
    options.reduce(
      (acc, option) => ({
        ...acc,
        [option.id]: option.id === 0 ? false : false,
      }),
      {}
    )
  );

  useEffect(() => {
    if (isResetCheckboxes) {
      setCheckboxes({});
    }
    setIsResetCheckboxes?.(false);
  }, [isResetCheckboxes, setIsResetCheckboxes]);

  // Handle individual checkbox change
  const handleCheckboxChange = (key: number, value: boolean) => {
    const updatedCheckboxes = {
      ...checkboxes,
      [key]: value,
    };

    // Check if all checkboxes are selected to update selectAll
    const allSelected = Object.values(updatedCheckboxes).every(
      (val, idx) => idx === 0 || val // Exclude selectAll (id: 0)
    );

    // Update selectAll checkbox based on individual selections
    updatedCheckboxes[0] = allSelected;

    setCheckboxes(updatedCheckboxes);
    onChange?.(updatedCheckboxes); // Pass the updated checkboxes state to the parent
  };

  // Handle selectAll checkbox change
  const handleSelectAll = (value: boolean) => {
    const updatedCheckboxes = options.reduce((acc, option) => {
      acc[option.id] = value;
      return acc;
    }, {} as Record<number, boolean>);

    setCheckboxes(updatedCheckboxes);
    onChange?.(updatedCheckboxes);
  };

  return (
    <div className="flex flex-col gap-3 h-full border border-gray-400 rounded-lg p-3">
      {options?.map((option) => (
        <div
          className="first:border-b first:border-gray-400 first:pb-2"
          key={option.id}
        >
          <CheckboxInput
            id={`${id}-${String(option.id)}`}
            label={option.label}
            labelStyles="md:text-base"
            checked={checkboxes[option.id] || false}
            setChecked={(value) => {
              if (typeof value === "function") {
                const resolvedValue = value(checkboxes[option.id]);
                option.id === 0
                  ? handleSelectAll(resolvedValue)
                  : handleCheckboxChange(option.id, resolvedValue);
              } else {
                option.id === 0
                  ? handleSelectAll(value)
                  : handleCheckboxChange(option.id, value);
              }
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default React.memo(CheckboxGroupInput);
